import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CustomFieldsEditor } from "@/features/servers/components/CustomFieldsEditor";
import { DicomEndpointsEditor } from "@/features/servers/components/DicomEndpointsEditor";
import { SecretInput } from "@/features/servers/components/SecretInput";
import type {
  CreateSoftwareInstanceInput,
  ICustomField,
  IDicomEndpoint,
  IHost,
  ISoftwareInstance,
  ISoftwareType,
} from "@/features/servers/schemas/servers";

interface SoftwareInstanceFormDialogProps {
  open: boolean;
  onClose: () => void;
  instance?: ISoftwareInstance | null;
  softwareTypes: ISoftwareType[];
  hosts: IHost[];
  onSubmit: (data: CreateSoftwareInstanceInput) => Promise<void>;
  isSaving?: boolean;
}

const EMPTY: CreateSoftwareInstanceInput = {
  softwareTypeId: "",
  hostId: "",
  active: true,
  mail: "",
  password: "",
  url: "",
  version: "",
  lastUpdatedAt: "",
  dicomEndpoints: [],
  customFields: [],
};

export function SoftwareInstanceFormDialog({
  open,
  onClose,
  instance,
  softwareTypes,
  hosts,
  onSubmit,
  isSaving = false,
}: SoftwareInstanceFormDialogProps) {
  const [form, setForm] = useState<CreateSoftwareInstanceInput>(EMPTY);

  const activeTypes = softwareTypes.filter((t) => t.active);

  useEffect(() => {
    if (!open) return;
    if (instance) {
      setForm({
        softwareTypeId: instance.softwareTypeId,
        hostId: instance.hostId ?? "",
        active: instance.active,
        mail: instance.mail ?? "",
        password: instance.password ?? "",
        url: instance.url ?? "",
        version: instance.version ?? "",
        lastUpdatedAt: instance.lastUpdatedAt ?? "",
        dicomEndpoints: instance.dicomEndpoints ?? [],
        customFields: instance.customFields ?? [],
      });
    } else {
      setForm({
        ...EMPTY,
        softwareTypeId: activeTypes[0]?.id ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on open/instance
  }, [open, instance]);

  const set = <K extends keyof CreateSoftwareInstanceInput>(
    key: K,
    value: CreateSoftwareInstanceInput[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!form.softwareTypeId) return;
    await onSubmit({
      ...form,
      hostId: form.hostId || undefined,
      customFields: (form.customFields ?? []).filter((f) => f.key.trim()),
      dicomEndpoints: (form.dicomEndpoints ?? []).filter((ep) => ep.aetitle.trim()),
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto gap-4">
        <DialogHeader>
          <DialogTitle className="text-base">
            {instance ? "Editar software" : "Agregar software"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Instancia del catálogo en este centro. Endpoints DICOM solo si aplica.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={form.softwareTypeId}
                onValueChange={(v) => set("softwareTypeId", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {activeTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Host</Label>
              <Select
                value={form.hostId || "__none__"}
                onValueChange={(v) => set("hostId", v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin host" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin host</SelectItem>
                  {hosts.map((h) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.name}
                      {h.ip ? ` (${h.ip})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="sw-mail">Mail / usuario ViewMed</Label>
              <Input
                id="sw-mail"
                value={form.mail ?? ""}
                onChange={(e) => set("mail", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sw-pass">Clave</Label>
              <SecretInput
                id="sw-pass"
                value={form.password ?? ""}
                onChange={(v) => set("password", v)}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="sw-url">URL</Label>
              <Input
                id="sw-url"
                value={form.url ?? ""}
                onChange={(e) => set("url", e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sw-version">Versión</Label>
              <Input
                id="sw-version"
                value={form.version ?? ""}
                onChange={(e) => set("version", e.target.value)}
                placeholder="0.0.0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sw-updated">Último update</Label>
              <div className="flex gap-2">
                <Input
                  id="sw-updated"
                  type="date"
                  value={form.lastUpdatedAt ?? ""}
                  onChange={(e) => set("lastUpdatedAt", e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() =>
                    set("lastUpdatedAt", new Date().toISOString().slice(0, 10))
                  }
                >
                  Hoy
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => set("active", v)}
                id="sw-active"
              />
              <Label htmlFor="sw-active">Activo</Label>
            </div>
          </div>

          <DicomEndpointsEditor
            endpoints={(form.dicomEndpoints as IDicomEndpoint[]) ?? []}
            onChange={(eps) => set("dicomEndpoints", eps)}
          />

          <CustomFieldsEditor
            fields={(form.customFields as ICustomField[]) ?? []}
            onChange={(fields) => set("customFields", fields)}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="cursor-pointer"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="cursor-pointer"
            onClick={() => void handleSave()}
            disabled={isSaving || !form.softwareTypeId}
          >
            {isSaving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
